import {
  BottomSheetScrollView,
  type BottomSheetScrollViewMethods,
} from '@gorhom/bottom-sheet'
import { useRef } from 'react'
import {
  Keyboard,
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Drawer } from '../flow/Drawer'
import { useNestedSheet } from '../hooks/useNestedSheet'
import { useNestedSheetHeight } from '../hooks/useNestedSheetHeight'
import { FigmaChevronRightIcon } from '../icons/FigmaSystemIcons'
import { Text, useTheme } from '../theme/restyle'
import { FiatCountryList } from './FiatCountrySheet'
import { defaultPhoneCountry } from './phoneCountries'

export interface FiatChoiceFieldProps {
  label: string
  title: string
  value: string
  options: readonly { id: string; labelFallback: string }[]
  editable: boolean
  invalid: boolean
  grouped?: boolean
  /**
   * The options are ISO 3166-1 alpha-2 codes. Every country on earth is
   * further than a plain list can be read through, so the drawer is the
   * searchable list with the index rail the phone flow already draws.
   */
  country?: boolean
  onChange: (value: string) => void
}

/** KYC R2 selection drawer, shared by country and provider questionnaires. */
export function FiatChoiceField({
  label,
  title,
  value,
  options,
  editable,
  invalid,
  grouped,
  country = false,
  onChange,
}: FiatChoiceFieldProps) {
  const theme = useTheme()
  const sheet = useNestedSheet<'options'>()
  const { nestedScreen, openNestedScreen, closeNestedScreen } = sheet
  const isOpen = nestedScreen !== null
  const selected = options.find((option) => option.id === value)
  const fill = invalid ? theme.form.fieldInvalid.fill : theme.form.field
  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityValue={
          selected === undefined ? undefined : { text: selected.labelFallback }
        }
        accessibilityState={{ expanded: isOpen, disabled: !editable }}
        disabled={!editable}
        onPress={() => {
          // The drawer covers the field it was typed into, and a keyboard left
          // standing behind it is one the user has to dismiss twice.
          Keyboard.dismiss()
          openNestedScreen('options')
        }}
        style={[
          styles.field,
          { backgroundColor: grouped ? 'transparent' : fill },
        ]}
      >
        <Text
          weight="medium"
          numberOfLines={1}
          style={[
            styles.fieldLabel,
            {
              color:
                selected === undefined
                  ? theme.content.elements.secondary
                  : theme.content.elements.primary,
            },
          ]}
        >
          {selected?.labelFallback ?? label}
        </Text>
        <FigmaChevronRightIcon
          size={16}
          color={theme.content.elements.secondary}
        />
      </Pressable>
      <Drawer sheet={sheet} fullHeight>
        {isOpen && editable && country ? (
          <FiatCountryList
            heading={title}
            selected={defaultPhoneCountry(value || undefined)}
            onSelect={(next) => {
              onChange(next.alpha2)
              closeNestedScreen()
            }}
            onClose={closeNestedScreen}
          />
        ) : null}
        {isOpen && editable && !country ? (
          <ChoiceList
            title={title}
            value={value}
            options={options}
            onChange={(next) => {
              onChange(next)
              closeNestedScreen()
            }}
          />
        ) : null}
      </Drawer>
    </>
  )
}

/** The answers the drawer holds, under the question they answer. */
function ChoiceList({
  title,
  value,
  options,
  onChange,
}: {
  title: string
  value: string
  options: readonly { id: string; labelFallback: string }[]
  onChange: (value: string) => void
}) {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const height = useNestedSheetHeight()
  const listRef = useRef<BottomSheetScrollViewMethods>(null)
  // The list opens on the answer it already holds, where the first row rests:
  // every country on earth opens on Afghanistan otherwise. Once, on the frame
  // the row is laid out — a later re-layout is not a reopening.
  const landed = useRef(false)
  const landOnAnswer = ({ nativeEvent }: LayoutChangeEvent) => {
    if (landed.current) {
      return
    }
    landed.current = true
    listRef.current?.scrollTo({ y: nativeEvent.layout.y, animated: false })
  }

  return (
    // The drawer's own surface is the glass; the screen paints no fill.
    <View style={{ height }}>
      <BottomSheetScrollView
        ref={listRef}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: insets.bottom + METRICS.screenInset },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <Text weight="medium" style={styles.heading}>
          {title}
        </Text>
        <View style={[styles.card, { backgroundColor: theme.form.field }]}>
          {options.map((option, index) => (
            <View
              key={option.id}
              onLayout={option.id === value ? landOnAnswer : undefined}
            >
              {index === 0 ? null : (
                <View
                  style={[
                    styles.divider,
                    { backgroundColor: theme.form.divider },
                  ]}
                />
              )}
              <Pressable
                accessibilityRole="radio"
                accessibilityLabel={option.labelFallback}
                accessibilityState={{ selected: option.id === value }}
                onPress={() => onChange(option.id)}
                style={styles.option}
              >
                <Text style={styles.optionLabel}>{option.labelFallback}</Text>
              </Pressable>
            </View>
          ))}
        </View>
      </BottomSheetScrollView>
    </View>
  )
}

/**
 * The header clears the grabber by the band every other drawer leaves, so the
 * question lands where the country list's heading does.
 */
const METRICS = {
  screenInset: 24,
  headerTop: 84,
  headingFontSize: 18,
  headingBottom: 12,
} as const

const styles = StyleSheet.create({
  field: {
    minHeight: 56,
    borderRadius: 16,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  fieldLabel: { flex: 1, fontSize: 17, letterSpacing: -0.34 },
  content: {
    paddingHorizontal: METRICS.screenInset,
    paddingTop: METRICS.headerTop,
    gap: METRICS.headingBottom,
  },
  heading: {
    paddingHorizontal: 16,
    fontSize: METRICS.headingFontSize,
    letterSpacing: -0.36,
  },
  card: { borderRadius: 24, overflow: 'hidden' },
  option: {
    minHeight: 56,
    paddingHorizontal: 16,
    paddingVertical: 18,
    justifyContent: 'center',
  },
  optionLabel: { fontSize: 17, lineHeight: 19.55, letterSpacing: -0.34 },
  divider: { height: 1, marginHorizontal: 16 },
})
